import { useCallback, useEffect, useRef } from 'react';
import {
  registerRouteScrollContainer,
  unregisterRouteScrollContainer,
} from '../utils/routeScrollRegistry';

type ScrollRegistration<T extends HTMLElement> = {
  containerId: string;
  element: T;
};

export const useRouteScrollContainer = <T extends HTMLElement>(containerId: string) => {
  const registrationRef = useRef<ScrollRegistration<T> | null>(null);

  const setRouteScrollContainer = useCallback((element: T | null) => {
    const previous = registrationRef.current;

    if (previous) {
      unregisterRouteScrollContainer(previous.containerId, previous.element);
      registrationRef.current = null;
    }

    if (!element) {
      return;
    }

    registerRouteScrollContainer(containerId, element);
    registrationRef.current = {
      containerId,
      element,
    };
  }, [containerId]);

  useEffect(() => {
    return () => {
      const activeRegistration = registrationRef.current;
      if (!activeRegistration) {
        return;
      }

      unregisterRouteScrollContainer(activeRegistration.containerId, activeRegistration.element);
      registrationRef.current = null;
    };
  }, []);

  return setRouteScrollContainer;
};
